// @flow

import type { Theme } from '../../types/theme';
import * as fontWeights from '../../helpers/fontWeight';
import fonts from './terbergbusiness.fonts';
import defaultTheme from '../default';

export {
  fonts,
};

export const BRAND_PRIMARY = '#1f549f';
export const BRAND_SECONDARY = '#00a4b7';
export const BRAND_TERTIARY = '#86bc25';

export const BRAND_PRIMARY_FROM = BRAND_PRIMARY;
export const BRAND_PRIMARY_TO = BRAND_PRIMARY;

export const BRAND_TERTIARY_FROM = BRAND_TERTIARY;
export const BRAND_TERTIARY_TO = BRAND_TERTIARY;

export const BRAND_PRIMARY_DARK = '#173e75';
export const BRAND_SECONDARY_DARK = '#027a88';
export const BRAND_TERTIARY_DARK = '#668f1d';

export const LIGHT_ORANGE = '#ffcaa4';
export const RED = '#ff0007';
export const LIGHT_RED = '#fedbdb';
export const GREEN = '#4ba82e';
export const LIGHT_GREEN = '#abef96';

export const WHITE = '#ffffff';
export const SILVER_LIGHT = '#f3f6f9';
export const SILVER = '#E9E8E8';
export const GRAY_LIGHT = '#c2c5cb';
export const GRAY_DARK = '#727c8c';
export const BLACK = '#232323';
export const LIGHT_BLUE = '#cfe8f6';
export const CLOUDY_BLUE = '#bcd2db';

export const FONT_DEFAULT = '"Open Sans", sans-serif';
export const FONT_HEADING = '"Open Sans", sans-serif';
export const FONT_ACCENT = '"Open Sans", sans-serif';

export const FONT_WEIGHT_DEFAULT = fontWeights.REGULAR;
export const FONT_WEIGHT_SUBHEADING = fontWeights.LIGHT;
export const FONT_WEIGHT_HEADING = fontWeights.SEMIBOLD;
export const FONT_WEIGHT_BOLD = fontWeights.MEDIUM;
export const FONT_WEIGHT_ACCENT = fontWeights.REGULAR;

export const FONT_LINEHEIGHT_DEFAULT = '1.1em';
export const FONT_LINEHEIGHT_TEXT = '1.6em';
export const FONT_LINEHEIGHT_TITLE = '1.3em';


export const FONT_SIZE_DEFAULT = '1.125rem';
export const FONT_SIZE_TINY = '0.75rem';
export const FONT_SIZE_SMALL = '0.875rem';
export const FONT_SIZE_H1 = '2.75rem';
export const FONT_SIZE_H2 = '2rem';
export const FONT_SIZE_H3 = '1.75rem';
export const FONT_SIZE_H4 = '1.5rem';
export const FONT_SIZE_H5 = '1.125rem';
export const FONT_SIZE_H6 = '1rem';

export const BOX_SHADOW = '0 0 7px 0 rgba(0, 0, 0, 0.3)';
export const DROP_SHADOW = '0 0 7px rgba(0, 0, 0, 0.3)';
export const BORDER_RADIUS_DEFAULT = '6px';
export const BORDER_RADIUS_SMALL = '4px';
export const BORDER_COLOR = SILVER;

export const TEXT_DEFAULT = BLACK;
export const TEXT_SECONDARY = GRAY_DARK;
export const TEXT_TERTIARY = SILVER;
export const TEXT_HERO = WHITE;
export const TEXT_LIGHT = WHITE;
export const HEADING_DEFAULT = BRAND_PRIMARY;
export const BACKGROUND_DEFAULT = WHITE;
export const BACKGROUND_LIGHT = SILVER_LIGHT;
export const BACKGROUND_DARK = BLACK;
export const BACKGROUND_ERROR = LIGHT_RED;
export const BACKGROUND_SUCCESS = LIGHT_GREEN;

export const PRIMARY_BTN_SCHEME = {
  main: BRAND_PRIMARY,
  hover: BRAND_PRIMARY_DARK,
  active: BRAND_PRIMARY_DARK,
  contrast: WHITE,
  visited: BRAND_PRIMARY,
};

export const PRIMARY_GRADIENT_SCHEME = {
  main: `linear-gradient(to right, ${BRAND_PRIMARY_FROM}, ${BRAND_PRIMARY_TO});`,
  hover: `linear-gradient(to right, ${BRAND_PRIMARY}, ${BRAND_PRIMARY});`,
  active: `linear-gradient(to right, ${BRAND_PRIMARY}, ${BRAND_PRIMARY});`,
  visited: `linear-gradient(to right, ${BRAND_PRIMARY}, ${BRAND_PRIMARY});`,
  contrast: WHITE,
};

export const SECONDARY_BTN_SCHEME = {
  main: BRAND_SECONDARY,
  hover: BRAND_SECONDARY_DARK,
  active: BRAND_SECONDARY_DARK,
  contrast: WHITE,
  visited: BRAND_SECONDARY,
};

export const TERTIARY_BTN_SCHEME = {
  main: BRAND_TERTIARY,
  hover: BRAND_TERTIARY_DARK,
  active: BRAND_TERTIARY_DARK,
  contrast: WHITE,
  visited: BRAND_TERTIARY,
};

export const LIGHT_BTN_SCHEME = {
  main: WHITE,
  hover: SILVER_LIGHT,
  active: SILVER_LIGHT,
  contrast: WHITE,
  visited: WHITE,
};

export const MAIN_MENU_SCHEME = {
  main: BRAND_SECONDARY,
  hover: BRAND_PRIMARY,
  active: BRAND_PRIMARY,
  contrast: BRAND_TERTIARY,
  visited: BRAND_PRIMARY,
};
export const SUB_MENU_SCHEME = {
  main: WHITE,
  hover: WHITE,
  active: WHITE,
  contrast: BRAND_TERTIARY,
  visited: WHITE,
};

export const DISABLED_BTN_SCHEME = {
  main: GRAY_LIGHT,
  hover: GRAY_LIGHT,
  active: GRAY_LIGHT,
  contrast: WHITE,
  visited: GRAY_LIGHT,
};

/*
 * COLOR SCHEMES
 */
export const DEFAULT_SCHEME = {
  background: 'transparent',
  border: SILVER_LIGHT,
  text: TEXT_DEFAULT,
  heading: HEADING_DEFAULT,
  anchor: BRAND_SECONDARY,
  shadow: BOX_SHADOW,
};

export const DEFAULT_LIGHT_SCHEME = {
  background: 'transparent',
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const LIGHT_SCHEME = {
  background: SILVER_LIGHT,
  border: SILVER,
  text: TEXT_DEFAULT,
  heading: HEADING_DEFAULT,
  anchor: BRAND_SECONDARY,
  shadow: BOX_SHADOW,
};

export const WHITE_SCHEME = {
  background: WHITE,
  border: SILVER_LIGHT,
  text: TEXT_DEFAULT,
  heading: HEADING_DEFAULT,
  anchor: BRAND_SECONDARY,
  shadow: BOX_SHADOW,
};

export const DARK_SCHEME = {
  background: BLACK,
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const PRIMARY_SCHEME = {
  background: `linear-gradient(to right, ${BRAND_PRIMARY_FROM}, ${BRAND_PRIMARY_TO});`,
  border: 'transparent',
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const SECONDARY_SCHEME = {
  background: BRAND_SECONDARY,
  border: SILVER_LIGHT,
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

export const TERTIARY_SCHEME = {
  background: `linear-gradient(to right, ${BRAND_TERTIARY_FROM}, ${BRAND_TERTIARY_TO});`,
  border: 'transparent',
  text: WHITE,
  heading: WHITE,
  icons: WHITE,
  anchor: WHITE,
  shadow: BOX_SHADOW,
};

const defaults = [{
  BRAND_PRIMARY,
  BRAND_SECONDARY,
  BRAND_SECONDARY_DARK,
  BRAND_TERTIARY,
  BRAND_PRIMARY_FROM,
  BRAND_PRIMARY_TO,
  WHITE,
  SILVER,
  GRAY: GRAY_LIGHT,
  GRAY_DARK,
  BLACK,
  ERROR: RED,
  SUCCESS: GREEN,
  FONT_DEFAULT,
  FONT_HEADING,
  FONT_ACCENT,
  FONT_WEIGHT_DEFAULT,
  FONT_WEIGHT_HEADING,
  FONT_WEIGHT_SUBHEADING,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_ACCENT,
  TEXT_DEFAULT,
  TEXT_SECONDARY,
  TEXT_TERTIARY,
  TEXT_HERO,
  TEXT_LIGHT,
  HEADING_DEFAULT,
  BACKGROUND_DEFAULT,
  BACKGROUND_LIGHT,
  BACKGROUND_DARK,
  BACKGROUND_ERROR,
  BACKGROUND_SUCCESS,
  FONT_SIZE_H1,
  FONT_SIZE_H2,
  FONT_SIZE_H3,
  FONT_SIZE_H4,
  FONT_SIZE_H5,
  FONT_SIZE_H6,
  FONT_SIZE_DEFAULT,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  FONT_LINEHEIGHT_DEFAULT,
  FONT_LINEHEIGHT_TEXT,
  FONT_LINEHEIGHT_TITLE,
  BOX_SHADOW,
  DROP_SHADOW,
  BORDER_RADIUS_DEFAULT,
  BORDER_RADIUS_SMALL,
  BORDER_COLOR,
  PRIMARY_BTN_SCHEME,
  PRIMARY_GRADIENT_SCHEME,
  SECONDARY_BTN_SCHEME,
  TERTIARY_BTN_SCHEME,
  LIGHT_BTN_SCHEME,
  MAIN_MENU_SCHEME,
  LIGHT_ORANGE,
  DISABLED_BTN_SCHEME,
  DEFAULT_SCHEME,
  DEFAULT_LIGHT_SCHEME,
  WHITE_SCHEME,
  LIGHT_SCHEME,
  DARK_SCHEME,
  PRIMARY_SCHEME,
  SECONDARY_SCHEME,
  TERTIARY_SCHEME,
}];

const terbergbusiness: Theme = defaultTheme(defaults, {
  buttons: {
    gradient: {
      default: PRIMARY_GRADIENT_SCHEME,
    },
    fontWeight: FONT_WEIGHT_HEADING,
    fontSize: FONT_SIZE_H6,
    scheme: {
      default: SECONDARY_BTN_SCHEME,
    },
  },
  card: {
    gradient: {
      default: WHITE_SCHEME,
    },
  },
  carblock: {
    buttonProps: {
      colors: 'primary',
    },
    title: {
      color: BRAND_SECONDARY,
    },
    price: {
      color: BRAND_TERTIARY,
    },
  },
  loader: {
    color: BRAND_SECONDARY,
  },
  table: {
    titleRow: {
      backgroundColor: SILVER_LIGHT,
    },
    titleCell: {
      backgroundColor: SILVER_LIGHT,
    },
  },
  pdbCompare: {
    subtitleColor: GRAY_DARK,
    removeIcon: GRAY_DARK,
  },
  wysiwyg: {
    fontSize: FONT_SIZE_DEFAULT,
    primary: BRAND_SECONDARY,
    templates: {
      default: {
        p: {
          fontSize: {
            default: FONT_SIZE_DEFAULT,
          },
          lineHeight: FONT_LINEHEIGHT_TEXT,
        },
        h1: {
          fontSize: {
            default: FONT_SIZE_H1,
          },
          lineHeight: FONT_LINEHEIGHT_TITLE,
        },
        h2: {
          fontSize: {
            default: FONT_SIZE_H2,
          },
          fontWeight: fontWeights.SEMIBOLD,
          lineHeight: FONT_LINEHEIGHT_TITLE,
        },
        h3: {
          fontSize: {
            default: FONT_SIZE_H3,
          },
          lineHeight: FONT_LINEHEIGHT_TITLE,
        },
        h4: {
          fontSize: {
            default: FONT_SIZE_H4,
          },
          fontWeight: fontWeights.LIGHT,
          color: BRAND_PRIMARY,
          margin: '1.5rem 0',
          lineHeight: FONT_LINEHEIGHT_TITLE,
        },
        h5: {
          fontSize: {
            default: FONT_SIZE_H5,
          },
          lineHeight: FONT_LINEHEIGHT_TITLE,
        },
        h6: {
          fontSize: {
            default: FONT_SIZE_H6,
          },
          lineHeight: FONT_LINEHEIGHT_TITLE,
        },
        subheadingfontWeight: fontWeights.LIGHT,
      },
      hero: {
        h1: {
          paddingBottom: '1rem',
          lineHeight: '3.5rem',
          fontWeight: fontWeights.LIGHT,
          fontFamily: FONT_ACCENT,
        },
        h2: {
          fontFamily: FONT_ACCENT,
          fontSize: FONT_SIZE_H3,
          fontWeight: fontWeights.LIGHT,
        },
        subheadingfontWeight: fontWeights.LIGHT,
      },
    },
  },
  assortment: {
    resultCount: {
      subtitleColor: GRAY_DARK,
    },
    filterBar: {
      backgroundColor: SILVER_LIGHT,
    },
    activeFilters: {
      backgroundColor: BRAND_TERTIARY,
      color: WHITE,
    },
    carBlock: {
      comparisonLabelBackground: SILVER_LIGHT,
    },
  },
  list: {
    title: {
      color: BRAND_SECONDARY,
    },
    subTitle: {
      color: BRAND_SECONDARY,
    },
    listStyles: {
      check: {
        color: GREEN,
      },
      plus: {
        color: GREEN,
      },
      cross: {
        color: RED,
      },
      minus: {
        color: RED,
      },
    },
  },
  review: {
    starColor: BRAND_PRIMARY,
    default: {
      ratingColor: BRAND_SECONDARY,
    },
  },
  deliveryTime: {
    color: BRAND_TERTIARY,
  },
  form: {
    password: {
      stages: {
        weak: RED,
        acceptable: BRAND_SECONDARY_DARK,
        strong: GREEN,
      },
    },
    rangeSlider: {
      progressColor: BRAND_TERTIARY,
      handleBorder: BRAND_TERTIARY,
    },
  },
  news: {
    articleBackgroundColor: WHITE,
    articleTitleColor: BRAND_SECONDARY,
  },
});

export default terbergbusiness;
